import React, { useCallback, useState } from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { Button } from '@picsio/ui';

import Icon from '../../../Icon';
import RadioItem from '../../../RadioItem';
import Checkbox from '../../../../UIComponents/checkbox';
import localization from '../../../../shared/strings';
import styles from './styles.module.scss';

const DELETE_MODE_TORIUT_ONLY = 'toriutOnly';
const DELETE_MODE_TORIUT_AND_CHANNELS = 'toriutAndChannels';

function DeleteOptionModal({
  type,
  name,
  channelsCount,
  channels,
  root,
  onConfirm,
}) {
  const strings = localization.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION;

  const [deleteMode, setDeleteMode] = useState(DELETE_MODE_TORIUT_ONLY);
  const [localChannels, setLocalChannels] = useState(
    () => channels.map((channel) => ({ ...channel, selected: true })),
  );

  const hasSelectedChannels = localChannels.some((channel) => channel.selected);
  const isDeleteDisabled = deleteMode === DELETE_MODE_TORIUT_AND_CHANNELS && !hasSelectedChannels;

  const title = type === 'option' ? strings.TITLE_PUBLISHED : strings.TITLE_PUBLISHED_VALUE;
  const bodyText = type === 'option'
    ? strings.TEXT_PUBLISHED(name, channelsCount)
    : strings.TEXT_PUBLISHED_VALUE(name, channelsCount);

  const destroy = useCallback(() => {
    try {
      root.unmount();
    } catch { /* ignore */ }
  }, [root]);

  const handleClose = useCallback(() => {
    destroy();
  }, [destroy]);

  const handleConfirm = useCallback(() => {
    const listingVariantIds = deleteMode === DELETE_MODE_TORIUT_AND_CHANNELS
      ? localChannels
        .filter((channel) => channel.selected)
        .flatMap((channel) => channel.listingVariantIds)
        .filter(Boolean)
      : [];

    destroy();
    onConfirm({ listingVariantIds });
  }, [deleteMode, destroy, localChannels, onConfirm]);

  const handleDeleteModeChange = useCallback((mode) => {
    setDeleteMode(mode);
  }, []);

  const handleChannelToggle = useCallback((channelId) => () => {
    setLocalChannels((prev) => prev.map(
      (channel) => (channel.id === channelId ? { ...channel, selected: !channel.selected } : channel),
    ));
  }, []);

  return (
    <div className="simpleDialog">
      <div className="simpleDialogUnderlayer" />
      <div className="simpleDialogBox">
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{title}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={handleClose}
            onKeyDown={handleClose}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <p>{bodyText}</p>
            <div className="m-t-16">
              <div className={styles.radioOption}>
                <RadioItem
                  value={DELETE_MODE_TORIUT_ONLY}
                  label={strings.RADIO_TORIUT_ONLY}
                  checkedValue={deleteMode}
                  onChange={handleDeleteModeChange}
                />
                <p className={styles.radioDescription}>
                  {strings.RADIO_TORIUT_ONLY_DESCRIPTION}
                </p>
              </div>
              <div className={styles.radioOption}>
                <RadioItem
                  value={DELETE_MODE_TORIUT_AND_CHANNELS}
                  label={strings.RADIO_TORIUT_AND_CHANNELS}
                  checkedValue={deleteMode}
                  onChange={handleDeleteModeChange}
                />
                <p className={styles.radioDescription}>
                  {strings.RADIO_TORIUT_AND_CHANNELS_DESCRIPTION}
                </p>
                <If condition={deleteMode === DELETE_MODE_TORIUT_AND_CHANNELS}>
                  <div className={styles.channelList}>
                    {localChannels.map((channel) => (
                      <Checkbox
                        key={channel.id}
                        value={channel.selected}
                        label={channel.name}
                        onChange={handleChannelToggle(channel.id)}
                      />
                    ))}
                  </div>
                </If>
              </div>
            </div>
          </div>
        </div>
        <div className="simpleDialogFooter">
          <Button
            color="secondary"
            size="md"
            variant="contained"
            onClick={handleClose}
          >
            {strings.BTN_CANCEL}
          </Button>
          <Button
            color="danger"
            size="md"
            variant="contained"
            disabled={isDeleteDisabled}
            onClick={handleConfirm}
          >
            {strings.BTN_OK}
          </Button>
        </div>
      </div>
    </div>
  );
}

DeleteOptionModal.propTypes = {
  type: PropTypes.oneOf(['option', 'optionValue']).isRequired,
  name: PropTypes.string.isRequired,
  channelsCount: PropTypes.number.isRequired,
  channels: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    listingVariantIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  })).isRequired,
  root: PropTypes.shape({ unmount: PropTypes.func.isRequired }).isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export function showDeleteOptionModal({
  type, name, channelsCount, channels, onConfirm,
}) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);
  root.render(
    <DeleteOptionModal
      type={type}
      name={name}
      channelsCount={channelsCount}
      channels={channels}
      root={root}
      onConfirm={onConfirm}
    />,
  );
}

export default DeleteOptionModal;
